import React from "react";
import styled from "styled-components";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { createGame } from "../../game/tetris";
import { useStore } from "../../store";
import { eventBus } from "../../game/eventBus";

const Main = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  max-width: 600px;
  margin: 0 auto;
  height: 100vh;
  max-height: 100vh;
  gap: 1rem;
  padding: 0 1rem 1rem;
`;

const Heading = styled.h1`
  font-size: 4rem;
  line-height: 1.1;
  // 3 rem if less than 550px
  /* @media (max-width: 550px) {
    font-size: 3rem;
  } */
`;

const Footer = styled.footer`
  font-size: 1.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  width: 100%;

  & a,
  & button {
    color: var(--color-white);
    font-size: 1.5rem;
    line-height: 1.1;
    transition: color 0.3s ease-in-out;
    text-decoration: underline;
    cursor: pointer;
  }

  /* & a:focus,
  & button:focus {
    outline: 2px solid var(--color-white);
    outline-offset: 2px;
  } */

  & a:hover,
  & button:hover {
    color: var(--color-teal);
  }
`;

const PhaserContainer = styled.div`
  min-height: 0;
  width: 100%;
  flex: 2;
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  gap: 2rem;
  opacity: ${(props) => (props.$startedOnce ? 1 : 0)};
  transition: opacity 0.5s ease-out;
`;

const PieceInformation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  max-height: 100%;
  min-height: 0;
  flex: 1;
  min-width: 0;
  gap: 0.25rem;
`;

const ScoreAndTimers = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  opacity: ${(props) => (props.$startedOnce ? 1 : 0)};
  transition: opacity 0.5s ease-out;
`;

const PauseText = styled.p`
  font-size: 1.5rem;
  opacity: var(--opacity);
  transition: opacity 0.2s ease-out;
`;

const ScoreText = styled.p`
  font-size: 1.5rem;
`;

const LevelText = styled.p`
  font-size: 1.5rem;
`;

const TimerText = styled.p`
  font-size: 1.5rem;
`;

const HeldPiece = styled.div`
  flex: 1;
  padding: 0.125rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 2px;
  padding: 8px;
`;

const NextPiece = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  place-items: center;
`;

const NextPieces = styled.div`
  display: flex;
  width: 100%;
  flex: 3;
  flex-direction: column;
  position: relative;
  align-items: center;
  border-radius: 2px;
  padding: 8px;
`;

const PieceSpacer = styled.div`
  flex: 1;
`;

const SidePieceImg = styled.img`
  display: block;
  object-fit: contain;
  flex: 1;
  min-width: 0;
  /* https://stackoverflow.com/questions/14068103/disable-antialising-when-scaling-images */
  image-rendering: optimizeSpeed; /* STOP SMOOTHING, GIVE ME SPEED  */
  image-rendering: -moz-crisp-edges; /* Firefox                        */
  image-rendering: -o-crisp-edges; /* Opera                          */
  image-rendering: -webkit-optimize-contrast; /* Chrome (and eventually Safari) */
  image-rendering: pixelated; /* Universal support since 2021   */
  image-rendering: optimize-contrast; /* CSS3 Proposed                  */
  -ms-interpolation-mode: nearest-neighbor; /* IE8+                           */
`;

const PieceGrid = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 0;
`;

const PieceLabel = styled.p`
  font-size: 1.5rem;
  position: absolute;
  top: 0%;
  left: 50%;
  transform: translate(-50%, 2px);
  color: var(--color-white);
`;

const GameOverText = styled.div`
  font-size: 1.5rem;
  position: absolute;
  top: 50%;
  left: var(--left);
  background-color: var(--background-color);
  padding: 0.5rem 2rem;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 3px solid var(--color-white);
  transform: translate(-50%, -50%);
`;

const UnstyledButton = styled.button`
  all: unset;
`;

const Button = styled.button`
  all: unset;
  /* border: 2px solid var(--color-white); */
  /* padding: 0.5rem 1rem; */
  /* border-radius: 3px; */
  background-color: var(--background-color);
  color: var(--color-white);
  font-size: 1.5rem;
  line-height: 1.1;
  cursor: pointer;

  transition:
    background-color 0.3s ease-in-out,
    color 0.3s ease-in-out;

  &:hover {
    background-color: var(--color-white);
    color: var(--background-color);
  }

  & span {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
  }
`;

// Dropdown Menu Styled Components
const DropdownMenuTrigger = styled(DropdownMenu.Trigger)`
  all: unset;
  color: var(--color-white);
  font-size: 1.5rem;
  line-height: 1.1;
  transition: color 0.3s ease-in-out;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--color-teal);
  }
`;

const DropdownMenuContent = styled(DropdownMenu.Content)`
  background-color: var(--background-color);
  border: 2px solid var(--color-white);
  border-radius: 6px;
  padding: 1rem;
  min-width: 250px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.5),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 50;
`;

const DropdownMenuLabel = styled(DropdownMenu.Label)`
  color: var(--color-white);
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  display: block;
`;

const DropdownMenuSeparator = styled(DropdownMenu.Separator)`
  height: 1px;
  background-color: var(--color-white);
  margin: 0.5rem 0;
  opacity: 0.3;
`;

const ControlItem = styled.div`
  color: var(--color-white);
  font-size: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ControlKey = styled.span`
  background-color: var(--color-white);
  color: var(--background-color);
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.875rem;
  font-weight: bold;
  min-width: 2rem;
  text-align: center;
`;

const ControlKeyList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
`;

const ControlDescription = styled.span`
  margin-left: 0.75rem;
  font-size: 1rem;
`;

const CloseButton = styled.button`
  all: unset;
  box-sizing: border-box;
  background-color: var(--color-white);
  color: var(--background-color);
  padding: 0.5rem 1rem;
  border-radius: 3px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 0.75rem;
  text-align: center;
  width: 100%;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--color-teal);
    color: var(--color-white);
  }
`;

const DropdownMenuArrow = styled(DropdownMenu.Arrow)`
  fill: var(--color-white);
  width: 18px;
  height: 9px;
  transform: translateY(-2px);
`;

function ControlsDropdown() {
  const [open, setOpen] = React.useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger>controls</DropdownMenuTrigger>
      <DropdownMenu.Portal>
        <DropdownMenuContent
          sideOffset={-5}
          onCloseAutoFocus={(e) => {
            // auto-focus is bad here because it'll cause us to re-open the dropdown
            // when hitting space to force-drop a piece
            e.preventDefault();
          }}
        >
          <DropdownMenuArrow />
          <DropdownMenuLabel>controls</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <ControlItem>
            <ControlKeyList>
              <ControlKey>A</ControlKey>
              <ControlKey>←</ControlKey>
            </ControlKeyList>
            <ControlDescription>left</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKeyList>
              <ControlKey>D</ControlKey>
              <ControlKey>→</ControlKey>
            </ControlKeyList>
            <ControlDescription>right</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKeyList>
              <ControlKey>W</ControlKey>
              <ControlKey>↑</ControlKey>
              <ControlKey>R</ControlKey>
            </ControlKeyList>
            <ControlDescription>rotate</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKeyList>
              <ControlKey>S</ControlKey>
              <ControlKey>↓</ControlKey>
            </ControlKeyList>
            <ControlDescription>soft drop</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKeyList>
              <ControlKey>Space</ControlKey>
            </ControlKeyList>
            <ControlDescription>hard drop</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKey>C</ControlKey>
            <ControlDescription>hold piece</ControlDescription>
          </ControlItem>

          <ControlItem>
            <ControlKey>P</ControlKey>
            <ControlDescription>pause/resume</ControlDescription>
          </ControlItem>

          <DropdownMenuSeparator />
          <CloseButton onClick={() => setOpen(false)}>close</CloseButton>
        </DropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const BASE_URL =
  process.env.NODE_ENV === "development" ? "/" : "/really-easy-tetris/";

function SidePiece({ color }) {
  return (
    <PieceGrid>
      {Array.from({ length: 4 }).map((_, i) => (
        <SidePieceImg key={i} src={`${BASE_URL}blocks/${color}.png`} />
      ))}
    </PieceGrid>
  );
}

function Timer() {
  const timer = useStore((state) => state.timer);
  return <TimerText>{timer}</TimerText>;
}

function GameOver() {
  const gameOver = useStore((state) => state.gameOver);
  const score = useStore((state) => state.score);
  if (!gameOver) return null;
  // canvas inside "phaser-game" div
  const canvasSize = document
    .querySelector("#phaser-game canvas")
    .getBoundingClientRect();
  const width = canvasSize.width;
  console.log("render");

  return (
    <GameOverText style={{ left: `${width / 2}px` }}>
      <p>game over</p>
      <p>score: {score}</p>
      <Button onClick={() => eventBus.emit("reset")}>
        <span>reset</span>
      </Button>
    </GameOverText>
  );
}

const StartGameText = styled.button`
  all: unset;
  background-color: var(--background-color);
  color: var(--color-white);
  padding: 0.5rem 1rem;
  border-radius: 3px;
  border: 2px solid var(--color-white);
  font-size: 1.5rem;
  line-height: 1.1;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

function StartGame() {
  const startedOnce = useStore((state) => state.startedOnce);
  if (startedOnce) return null;
  return (
    <StartGameText onClick={() => useStore.getState().setStartedOnce()}>
      start game
    </StartGameText>
  );
}

function App() {
  const gameRef = React.useRef(null);
  const paused = useStore((state) => state.paused);
  const score = useStore((state) => state.score);
  const level = useStore((state) => state.level);
  const nextPieces = useStore((state) => state.nextPieces);
  const heldPiece = useStore((state) => state.heldPiece);
  const startedOnce = useStore((state) => state.startedOnce);

  React.useLayoutEffect(() => {
    if (!startedOnce) return;
    if (gameRef.current) return;
    gameRef.current = createGame();

    setTimeout(() => {
      gameRef.current.scale.refresh();
    }, 100);

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        gameRef.current.scale.refresh();
      }, 20);
    });
    resizeObserver.observe(document.querySelector("#phaser-game canvas"));

    return () => {
      resizeObserver.disconnect();
    };
  }, [startedOnce]);

  return (
    <Main>
      <Heading>really easy tetris</Heading>
      <ScoreAndTimers $startedOnce={startedOnce}>
        <ScoreText>S:{score}</ScoreText>
        <LevelText>L:{level}</LevelText>
        <Timer />
        <PauseText style={{ opacity: paused ? 1 : 0 }}>(p)</PauseText>
      </ScoreAndTimers>
      <PhaserContainer $startedOnce={startedOnce}>
        <div style={{ flex: 3, position: "relative" }} id="phaser-game">
          <GameOver />
        </div>
        <PieceInformation>
          <HeldPiece>
            <PieceLabel>held</PieceLabel>
            {heldPiece && <SidePiece color={heldPiece} />}
          </HeldPiece>
          <NextPieces>
            <PieceLabel>next</PieceLabel>
            {nextPieces.map((color, i) => (
              <NextPiece key={i} color={color}>
                <SidePiece color={color} />
              </NextPiece>
            ))}
          </NextPieces>
          <PieceSpacer />
        </PieceInformation>
      </PhaserContainer>
      <StartGame />
      <Footer>
        <UnstyledButton onClick={() => useStore.getState().togglePaused()}>
          {paused ? "resume" : "pause"}
        </UnstyledButton>
        <ControlsDropdown />
        <a href="https://eieio.games">by eieio</a>
      </Footer>
    </Main>
  );
}

export default App;
